import { useMemo } from "react";
import { useAppSelector } from "@/store/hooks";

export interface UseFilterOptionsParams {
  /**
   * Geradores selecionados no filtro; quando preenchido, só aparecem
   * estabelecimentos desses geradores. Aceita tanto `external_id` (telas
   * legadas) quanto o id numérico (dashboard).
   */
  selectedGeneratorExternalIds?: string[];
}

export interface EstablishmentOption {
  /** `external_id` — chave usada pelas telas legadas (coletas, financeiro, indicadores). */
  establishment_id: string;
  /** Id numérico, usado pelo dashboard (fica legível na URL). */
  establishment_numeric_id: string;
  establishment_name: string;
  id_new_engine_generators: string;
}

export interface GeneratorOption {
  /** `external_id` — chave usada pelas telas legadas. */
  generator_id: string;
  /** Id numérico, usado pelo dashboard (fica legível na URL). */
  generator_numeric_id: string;
  generator_name: string;
}

/**
 * Ids dos estabelecimentos que pertencem aos geradores informados.
 * Sem gerador selecionado devolve `null`, que significa "sem restrição".
 *
 * Fonte única da cascata Gerador → Unidade: usada pelo hook abaixo e pela barra
 * de filtros do dashboard, que precisa saber quais unidades continuam válidas
 * ao trocar de gerador.
 *
 * Casa o gerador por `external_id` **ou** por id numérico, e devolve as duas
 * representações de cada estabelecimento — as telas legadas filtram por
 * `external_id` e o dashboard por id numérico, sobre o mesmo conjunto.
 */
export const getEstablishmentIdsForGenerators = (
  rawEstablishments: {
    id?: string | number;
    external_id: string;
    parent_id?: string | number | null;
  }[],
  rawGenerators: { id: string | number; external_id: string }[],
  selectedGenerators: string[],
): Set<string> | null => {
  if (selectedGenerators.length === 0) return null;

  const generatorIds = new Set(
    rawGenerators
      .filter(
        (g) =>
          selectedGenerators.includes(g.external_id) ||
          selectedGenerators.includes(String(g.id)),
      )
      .map((g) => Number(g.id)),
  );

  const allowed = new Set<string>();
  rawEstablishments
    .filter((e) => e.parent_id != null && generatorIds.has(Number(e.parent_id)))
    .forEach((e) => {
      allowed.add(e.external_id);
      if (e.id != null) allowed.add(String(e.id));
    });
  return allowed;
};

export const useFilterOptions = (params?: UseFilterOptionsParams) => {
  const {
    establishments: rawEstablishments,
    generators: rawGenerators,
    states: rawStates,
    materials: rawMaterials,
  } = useAppSelector((s) => s.establishmentAccess);
  const selectedGeneratorExternalIds = params?.selectedGeneratorExternalIds;

  // Estabelecimentos do estado global (Redux) — filtro por escopo do usuário é feito pelo backend via JWT;
  // aqui apenas aplicamos o filtro de UI por gerador(es) selecionados, se houver.
  const establishments: EstablishmentOption[] = useMemo(() => {
    const allowedIds = getEstablishmentIdsForGenerators(
      rawEstablishments,
      rawGenerators,
      selectedGeneratorExternalIds ?? [],
    );
    const filtered = allowedIds
      ? rawEstablishments.filter((e) => allowedIds.has(e.external_id))
      : rawEstablishments;

    const list = filtered.map((e) => ({
      establishment_id: e.external_id,
      establishment_numeric_id: String(e.id),
      establishment_name: e.name,
      id_new_engine_generators: e.id,
    }));
    return [...list].sort((a, b) =>
      a.establishment_name.localeCompare(b.establishment_name),
    );
  }, [rawEstablishments, rawGenerators, selectedGeneratorExternalIds]);

  // Geradores do estado global (Redux) — escopo já aplicado pelo backend via JWT
  const generators: GeneratorOption[] = useMemo(() => {
    return [...rawGenerators]
      .map((g) => ({
        generator_id: g.external_id,
        generator_numeric_id: String(g.id),
        generator_name: g.name,
      }))
      .sort((a, b) => a.generator_name.localeCompare(b.generator_name));
  }, [rawGenerators]);

  // Materiais do estado global (Redux) — value = id (para API), label = nome (exibição)
  const materialOptions = useMemo(
    () =>
      [...rawMaterials]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((m) => ({ value: m.id, label: m.name })),
    [rawMaterials],
  );

  // Estados (UF) do estado global (Redux) — vindos de GET /generators/me da generator-api (value = sigla, name = nome completo)
  const states = useMemo(
    () =>
      [...rawStates]
        .sort((a, b) => a.value.localeCompare(b.value))
        .map((s) => ({ value: s.value, label: s.name })),
    [rawStates],
  );

  return {
    establishments,
    materialOptions,
    states,
    generators,
  };
};
